import { Tag, type TagRootPropsInterface } from '@zillow/constellation-10';
import React from 'react';
import useConsistentCancellationsUiEnabled from '../../hooks/useConsistentCancellationsUiEnabled';

type Props = {
  linkId: string;
  text: string;
};

type StatusLabelTreatment = {
  text: string;
  tone: TagRootPropsInterface['tone'];
};

export const STATUS_LABEL_TOUR_CANCELED = 'statusLabelTourCanceled';
export const STATUS_LABEL_TOUR_DECLINED = 'statusLabelTourDeclined';
export const STATUS_LABEL_TOUR = 'statusLabelTour';

export const getTagAppearanceValueByLinkId = (linkId: string): TagRootPropsInterface['tone'] => {
  switch (linkId) {
    // Critical label
    case 'statusLabelAwaitingYourSignature':
    case STATUS_LABEL_TOUR_DECLINED:
      return 'critical';

    // Gray label
    case 'statusLabelExpired':
    case 'statusLabelInvitedToApply':
    case 'statusLabelLeaseCanceled':
    case 'statusLabelPayments':
    case 'statusLabelToured':
    case 'statusLabelTourRequested':
    case 'statusLabelApplicationWithdrawn':
    case STATUS_LABEL_TOUR_CANCELED:
      return 'gray';

    // Info label
    case 'statusLabelSigningInProgress':
    case STATUS_LABEL_TOUR:
      return 'info';

    // Warning label
    case 'statusLabelTourExpired':
    case 'statusResponseOverdue':
    case 'statusLabelApplicationConditionallyApproved':
      return 'warning';

    // Purple label
    case 'statusLabelTenant':
      return 'purple';

    // Success label
    case 'statusLabelApproved':
    case 'statusLabelLease':
    case 'statusLabelApplicationReceived':
    case 'statusLabelApplicationApproved':
      return 'success';

    // Deprecate
    case 'statusLabelReceived':
      return 'success';
    case 'statusLabelWithdrawn':
      return 'gray';
    default:
      return 'gray';
  }
};

/**
 * Replaces the backend's uppercase `statusLabel.text` with the design's sentence-case copy and
 * critical tone, matching `getTourStatusTagLabel`/`getTourStatusTone` in `@zg-rentals/touring`.
 * Keyed on `linkId` — which the optimistic cache write and the server payload both set — so the
 * chip looks identical right after cancelling and after a reload.
 */
export const getConsistentCancellationsTreatment = (linkId: string): StatusLabelTreatment | undefined => {
  switch (linkId) {
    case STATUS_LABEL_TOUR_CANCELED:
      return { text: 'Canceled tour', tone: 'critical' };
    default:
      return undefined;
  }
};

const StatusLabel = ({ text, linkId }: Props): JSX.Element => {
  const isConsistentCancellationsUiEnabled = useConsistentCancellationsUiEnabled();
  const treatment = isConsistentCancellationsUiEnabled ? getConsistentCancellationsTreatment(linkId) : undefined;

  return (
    <Tag tone={treatment?.tone ?? getTagAppearanceValueByLinkId(linkId)} size="sm" css={{ verticalAlign: 'middle' }}>
      {treatment?.text ?? text}
    </Tag>
  );
};

export default React.memo(StatusLabel);
